// src/components/admin/orders/OrderGroupingPanel.js
import { useState, useEffect, useRef } from 'react';
import { showToast } from '../../contexts/ToastNotification';
import { useOrderDetails, useOrderGrouping } from '../../components/hooks/hooks-admin/useAdminOrders';
import './orders-panel.scss';

export function OrderGroupingPanel({ onGroupCreated, onStartPickup }) {
    // Usa l'hook per il raggruppamento
    const {
        availableOrders,
        optimizedRoute,
        showRoute,
        loadingRoute,
        loadingOrders,
        groupingConfig,
        loadGroupableOrders,
        calculateOptimizedRoute,
        createPickupGroup,
        updateDaysBack,
        formatCurrency,
        hasOrders,
        totalOrders
    } = useOrderGrouping();
    
    // Usa l'hook per i dettagli ordine
    const {
        selectedOrder,
        loading: loadingDetail,
        fetchOrderDetail,
        resetSelectedOrder,
        formatDate
    } = useOrderDetails();
    
    // State locali
    const [showOrderDetail, setShowOrderDetail] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const panelRef = useRef(null);

    // Carica ordini all'avvio
    useEffect(() => {
        loadGroupableOrders();
    }, [loadGroupableOrders]);

    // Gestisce l'apertura del dettaglio ordine
    const handleViewOrderDetail = async (orderId) => {
        const order = await fetchOrderDetail(orderId);
        if (order) {
            setShowOrderDetail(true);
        }
    };

    // Gestisce la chiusura del dettaglio
    const handleCloseDetail = () => {
        setShowOrderDetail(false);
        resetSelectedOrder();
    };

    // Gestisce il calcolo del percorso
    const handleCalculateRoute = async () => {
        const orderIds = availableOrders.map(o => o.id);
        await calculateOptimizedRoute(orderIds, groupingConfig.daysBack);
    };

    // Gestisce la creazione del gruppo
    const handleCreateGroup = async () => {
        const orderIds = availableOrders.map(o => o.id);
        const group = await createPickupGroup(orderIds, optimizedRoute, (groupData) => {
            if (onGroupCreated) {
                onGroupCreated(groupData);
            }
        });
        
        if (group && onStartPickup && optimizedRoute) {
            onStartPickup(optimizedRoute);
        }
    };

    // Gestisce il cambio dei giorni
    const handleDaysChange = async (e) => {
        const days = parseInt(e.target.value);
        await updateDaysBack(days);
    };

    // 🖨️ Stampa percorso
    const printRoute = () => {
        if (!optimizedRoute) return;
        
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            showToast('Impossibile aprire la finestra di stampa. Permetti i popup.', 'error');
            return;
        }

        const currentDate = new Date().toLocaleString('it-IT');
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Percorso Ritiro - ${currentDate}</title>
                <meta charset="UTF-8">
                <style>
                    body {
                        font-family: 'Segoe UI', Arial, sans-serif;
                        margin: 40px;
                        color: #333;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                        border-bottom: 2px solid #667eea;
                        padding-bottom: 20px;
                    }
                    .header h1 {
                        margin: 0;
                        color: #667eea;
                    }
                    .header p {
                        margin: 5px 0 0;
                        color: #666;
                    }
                    .route-summary {
                        background: #f5f5f5;
                        padding: 15px;
                        border-radius: 8px;
                        margin-bottom: 25px;
                        display: flex;
                        justify-content: space-around;
                        flex-wrap: wrap;
                    }
                    .summary-item {
                        text-align: center;
                    }
                    .summary-label {
                        font-size: 12px;
                        color: #666;
                        text-transform: uppercase;
                    }
                    .summary-value {
                        font-size: 20px;
                        font-weight: bold;
                        color: #667eea;
                    }
                    .route-stops {
                        margin-top: 20px;
                    }
                    .route-stops h3 {
                        color: #333;
                        border-left: 4px solid #667eea;
                        padding-left: 12px;
                    }
                    .stop-item {
                        margin: 15px 0;
                        padding: 12px;
                        background: #fafafa;
                        border-radius: 6px;
                        page-break-inside: avoid;
                    }
                    .stop-number {
                        display: inline-block;
                        width: 30px;
                        height: 30px;
                        background: #667eea;
                        color: white;
                        border-radius: 50%;
                        text-align: center;
                        line-height: 30px;
                        font-weight: bold;
                        margin-right: 12px;
                    }
                    .vendor-name {
                        font-weight: bold;
                        font-size: 16px;
                    }
                    .vendor-address {
                        color: #666;
                        font-size: 14px;
                        margin-top: 4px;
                    }
                    .order-count {
                        background: #e0e0e0;
                        padding: 2px 8px;
                        border-radius: 12px;
                        font-size: 12px;
                        margin-left: 10px;
                    }
                    .footer {
                        margin-top: 40px;
                        text-align: center;
                        font-size: 12px;
                        color: #999;
                        border-top: 1px solid #ddd;
                        padding-top: 20px;
                    }
                    @media print {
                        body {
                            margin: 20px;
                        }
                        .no-print {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>🗺️ Piano di Ritiro - Abruzzo</h1>
                    <p>Generato il ${currentDate}</p>
                    <p>Finestra: ultimi ${groupingConfig.daysBack} ${groupingConfig.daysBack === 1 ? 'giorno' : 'giorni'}</p>
                </div>
                
                <div class="route-summary">
                    <div class="summary-item">
                        <div class="summary-label">Distanza totale</div>
                        <div class="summary-value">${optimizedRoute.totalDistance} km</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">Tempo stimato</div>
                        <div class="summary-value">${optimizedRoute.totalDuration} min</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">Venditori da visitare</div>
                        <div class="summary-value">${optimizedRoute.vendors.length}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">Ordini totali</div>
                        <div class="summary-value">${totalOrders}</div>
                    </div>
                </div>
                
                <div class="route-stops">
                    <h3>📍 Percorso suggerito</h3>
                    ${optimizedRoute.vendors.map((vendor, idx) => `
                        <div class="stop-item">
                            <div class="stop-number">${idx + 1}</div>
                            <div>
                                <div class="vendor-name">
                                    ${vendor.nome}
                                    <span class="order-count">${vendor.orderCount} ordini</span>
                                </div>
                                <div class="vendor-address">📍 ${vendor.indirizzo}</div>
                                ${vendor.telefono ? `<div class="vendor-phone">📞 ${vendor.telefono}</div>` : ''}
                                ${vendor.orario_ritiro ? `<div class="vendor-hours">🕒 ${vendor.orario_ritiro}</div>` : ''}
                            </div>
                        </div>
                    `).join('')}
                </div>
                
                <div class="footer">
                    <p>Percorso ottimizzato - Generato automaticamente dal sistema di gestione ordini</p>
                    <p>Data ritiro consigliata: ${new Date(Date.now() + 86400000).toLocaleDateString('it-IT')}</p>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
        showToast('Percorso inviato alla stampante', 'success');
    };

    // 📱 Condividi percorso
    const shareRoute = async () => {
        if (!optimizedRoute) return;

        setIsSharing(true);
        
        const routeText = `
📍 PERCORSO RITIRO ABRUZZO
📅 Data: ${new Date().toLocaleDateString('it-IT')}
📦 Ordini: ${totalOrders}
🏪 Tappe: ${optimizedRoute.vendors.length}
📏 Distanza: ${optimizedRoute.totalDistance} km
⏱️ Tempo: ${optimizedRoute.totalDuration} min

🗺️ PERCORSO:
${optimizedRoute.vendors.map((v, i) => `${i+1}. ${v.nome} - ${v.indirizzo} (${v.orderCount} ordini)`).join('\n')}
        `;

        const waypoints = optimizedRoute.vendors.map(v => 
            encodeURIComponent(v.indirizzo)
        ).join('|');
        
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${waypoints.split('|')[0]}&destination=${waypoints.split('|').pop()}&waypoints=${waypoints.split('|').slice(1, -1).join('|')}&travelmode=driving`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Percorso Ritiro Abruzzo',
                    text: routeText,
                    url: mapsUrl
                });
                showToast('Percorso condiviso con successo!', 'success');
            } catch (error) {
                if (error.name !== 'AbortError') {
                    fallbackShare(routeText, mapsUrl);
                }
            }
        } else {
            fallbackShare(routeText, mapsUrl);
        }
        
        setIsSharing(false);
    };

    const fallbackShare = (text, url) => {
        const shareData = `${text}\n\n📍 Apri in Google Maps:\n${url}`;
        navigator.clipboard.writeText(shareData).then(() => {
            showToast('Percorso copiato negli appunti!', 'success');
        }).catch(() => {
            showToast('Non è stato possibile copiare il percorso', 'error');
        });
    };

    // 📷 Genera QR Code
    const generateQRCode = () => {
        if (!optimizedRoute) return;
        
        const waypoints = optimizedRoute.vendors.map(v => 
            encodeURIComponent(v.indirizzo)
        ).join('|');
        
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${waypoints.split('|')[0]}&destination=${waypoints.split('|').pop()}&waypoints=${waypoints.split('|').slice(1, -1).join('|')}&travelmode=driving`;
        
        const qrWindow = window.open('', '_blank', 'width=450,height=550');
        if (!qrWindow) {
            showToast('Impossibile aprire la finestra. Permetti i popup.', 'error');
            return;
        }
        
        qrWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>QR Code - Percorso Ritiro</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                    body {
                        font-family: 'Segoe UI', Arial, sans-serif;
                        text-align: center;
                        padding: 20px;
                        background: #f5f5f5;
                    }
                    .container {
                        background: white;
                        border-radius: 16px;
                        padding: 20px;
                        box-shadow: 0 4px 20px rgba(0,0,0,0.1);
                    }
                    h3 {
                        color: #667eea;
                        margin-bottom: 20px;
                    }
                    #qrcode {
                        display: flex;
                        justify-content: center;
                        margin: 20px 0;
                    }
                    .url {
                        font-size: 12px;
                        color: #666;
                        word-break: break-all;
                        margin-top: 20px;
                        padding: 10px;
                        background: #f0f0f0;
                        border-radius: 8px;
                    }
                    .info {
                        font-size: 14px;
                        color: #333;
                        margin: 10px 0;
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <h3>📱 Scannerizza per aprire il percorso</h3>
                    <div class="info">
                        📦 ${totalOrders} ordini | 🏪 ${optimizedRoute.vendors.length} tappe
                    </div>
                    <div id="qrcode"></div>
                    <div class="url">
                        🔗 ${mapsUrl.substring(0, 80)}...
                    </div>
                </div>
                <script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"></script>
                <script>
                    new QRCode(document.getElementById("qrcode"), {
                        text: "${mapsUrl.replace(/"/g, '&quot;')}",
                        width: 280,
                        height: 280
                    });
                </script>
            </body>
            </html>
        `);
    };

    // 📄 Esporta JSON
    const exportAsJSON = () => {
        const exportData = {
            generatedAt: new Date().toISOString(),
            config: groupingConfig,
            summary: {
                totalOrders: totalOrders,
                totalVendors: optimizedRoute?.vendors.length || 0,
                totalDistance: optimizedRoute?.totalDistance,
                totalDuration: optimizedRoute?.totalDuration
            },
            route: optimizedRoute,
            orders: availableOrders
        };
        
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', `percorso-ritiro-${new Date().toISOString().slice(0,19)}.json`);
        linkElement.click();
        
        showToast('Percorso esportato come JSON', 'success');
    };

    // Se non ci sono ordini e non sta caricando, mostra messaggio vuoto
    if (!hasOrders && !loadingOrders) {
        return (
            <div className="order-grouping-panel empty">
                <div className="empty-icon">🗺️</div>
                <h4>Nessun ordine da ritirare</h4>
                <p>Non ci sono ordini in attesa di ritiro presso i fornitori negli ultimi {groupingConfig.daysBack} giorni.</p>
            </div>
        );
    }

    return (
        <div className="order-grouping-panel" ref={panelRef}>
            <div className="grouping-header">
                <div className="grouping-icon">🗺️</div>
                <div className="grouping-info">
                    <h3>Raggruppamento Ritiri Abruzzo</h3>
                    <p>
                        {loadingOrders ? 'Caricamento ordini...' : 
                            `${totalOrders} ordini pronti per il ritiro 
                            (ultimi ${groupingConfig.daysBack} ${groupingConfig.daysBack === 1 ? 'giorno' : 'giorni'})`}
                    </p>
                </div>
                <div className="grouping-controls">
                    <select
                        value={groupingConfig.daysBack}
                        onChange={handleDaysChange}
                        className="days-select"
                        disabled={loadingOrders}
                    >
                        <option value={1}>📅 Ultimo giorno</option>
                        <option value={2}>📅 Ultimi 2 giorni</option>
                        <option value={3}>📅 Ultimi 3 giorni</option>
                        <option value={5}>📅 Ultimi 5 giorni</option>
                        <option value={7}>📅 Ultima settimana</option>
                    </select>
                    <button 
                        onClick={handleCalculateRoute}
                        disabled={loadingRoute || !hasOrders}
                        className="route-btn"
                    >
                        {loadingRoute ? '⏳ Calcolo...' : '🚀 Calcola Percorso Ottimizzato'}
                    </button>
                    {optimizedRoute && (
                        <button onClick={handleCreateGroup} className="create-group-btn">
                            ✅ Crea Gruppo Ritiro
                        </button>
                    )}
                </div>
            </div>

            {/* Lista ordini disponibili raggruppati per fornitore */}
            {hasOrders && (
                <div className="available-orders-list">
                    <h4>📋 Ordini da ritirare ({totalOrders})</h4>
                    <div className="orders-list">
                        {availableOrders.map(order => (
                            <div key={order.id} className="order-item">
                                <div className="order-info">
                                    <span className="order-number">#{order.numero_ordine}</span>
                                    <span className="order-customer">
                                        {order.nome_utente} {order.cognome_utente}
                                    </span>
                                    <span className="order-amount">
                                        {formatCurrency(order.importo_totale)}
                                    </span>
                                    <span className={`order-status status-${order.stato}`}>
                                        {order.stato === 'in_attesa' && '📝 In attesa'}
                                        {order.stato === 'in_elaborazione' && '⚙️ In elaborazione'}
                                        {order.stato === 'pronto_ritiro' && '🏭 Pronto ritiro'}
                                    </span>
                                </div>
                                <div className="order-actions">
                                    <button 
                                        onClick={() => handleViewOrderDetail(order.id)}
                                        className="detail-btn"
                                        title="Visualizza dettagli"
                                        disabled={loadingDetail}
                                    >
                                        {loadingDetail ? '⏳...' : '🔍 Dettagli'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Percorso ottimizzato */}
            {showRoute && optimizedRoute && (
                <div className="optimized-route-display">
                    <div className="route-summary">
                        <span>📏 Distanza totale: {optimizedRoute.totalDistance} km</span>
                        <span>⏱️ Tempo stimato: {optimizedRoute.totalDuration} min</span>
                        <span>🏪 Fornitori da visitare: {optimizedRoute.vendors.length}</span>
                    </div>
                    
                    <div className="route-toolbar">
                        <button onClick={printRoute} className="tool-btn print-btn" title="Stampa percorso">
                            🖨️ Stampa
                        </button>
                        <button onClick={shareRoute} disabled={isSharing} className="tool-btn share-btn" title="Condividi percorso">
                            {isSharing ? '⏳...' : '📱 Condividi'}
                        </button>
                        <button onClick={generateQRCode} className="tool-btn qr-btn" title="Genera QR Code">
                            📷 QR Code
                        </button>
                        <button onClick={exportAsJSON} className="tool-btn export-btn" title="Esporta come JSON">
                            📄 Esporta JSON
                        </button>
                    </div>
                    
                    <div className="route-stops">
                        <h4>📍 Percorso suggerito:</h4>
                        <ol>
                            {optimizedRoute.vendors.map((vendor, idx) => (
                                <li key={idx}>
                                    <strong>{vendor.nome}</strong> - {vendor.indirizzo}
                                    <span className="order-count">
                                        ({vendor.orderCount} ordini)
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>
                    
                    <button 
                        onClick={() => onStartPickup?.(optimizedRoute)}
                        className="start-pickup-btn"
                    >
                        📍 Avvia Ritiro con Navigazione
                    </button>
                </div>
            )}

            {/* Modal Dettaglio Ordine */}
            {showOrderDetail && selectedOrder && (
                <div className="order-detail-modal-overlay" onClick={handleCloseDetail}>
                    <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Dettaglio Ordine #{selectedOrder.numero_ordine}</h3>
                            <button className="close-btn" onClick={handleCloseDetail}>✕</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="detail-section">
                                <h4>👤 Cliente</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Nome:</label>
                                        <span>{selectedOrder.cliente?.nome} {selectedOrder.cliente?.cognome}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Email:</label>
                                        <span>{selectedOrder.cliente?.email}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Telefono:</label>
                                        <span>{selectedOrder.cliente?.telefono || '—'}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="detail-section">
                                <h4>📋 Info Ordine</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Data creazione:</label>
                                        <span>{formatDate(selectedOrder.data_creazione)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Metodo pagamento:</label>
                                        <span>{selectedOrder.metodo_pagamento || '—'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Stato:</label>
                                        <span className={`status-badge status-${selectedOrder.stato}`}>
                                            {selectedOrder.stato}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="detail-section">
                                <h4>🛍️ Prodotti</h4>
                                <table className="products-table">
                                    <thead>
                                        <tr>
                                            <th>Prodotto</th>
                                            <th>Q.tà</th>
                                            <th>Prezzo unit.</th>
                                            <th>Totale</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOrder.items?.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{item.nome} {item.sku && <span className="sku">(SKU: {item.sku})</span>}</td>
                                                <td>{item.quantita}</td>
                                                <td>{item.formattedPrice || formatCurrency(item.prezzo_unitario)}</td>
                                                <td>{item.formattedTotal || formatCurrency(item.prezzo_totale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="3" className="total-label">Subtotale:</td>
                                            <td>{selectedOrder.formattedSubtotal || formatCurrency(selectedOrder.subtotale)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="3" className="total-label">Spedizione:</td>
                                            <td>{selectedOrder.formattedShipping || formatCurrency(selectedOrder.costo_spedizione)}</td>
                                        </tr>
                                        <tr className="grand-total">
                                            <td colSpan="3" className="total-label">Totale:</td>
                                            <td>{selectedOrder.formattedTotal || formatCurrency(selectedOrder.totale)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
